import { IMedia } from 'api/types/_news';
import { CONFIG_OPTION_TOAST_ERROR } from 'common/toast';
import DropdownOption from 'components/Common/DropdownOption';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import * as Yup from "yup";

interface ModalPartnerProps {
    isOpen: boolean;
    isPublishing: boolean;
    medias?: IMedia[],
    onPublish?: (info: {
        media_id: number,
        media_code_id: number,
        reporter_id?: number,
    }) => void,
    onCloseClick?: () => void,
}

const ModalPartner: React.FC<ModalPartnerProps> = ({ medias, isOpen, isPublishing, onPublish, onCloseClick }) => {
    const { t, i18n } = useTranslation();

    const [options, setOptions] = useState<any>([]);

    const handleCloseClick = () => {
        onCloseClick && onCloseClick();
    }

    const handleSubmit = async (values: any) => {
        try {
            if (!!isPublishing) {
                return;
            }
            onPublish && onPublish({
                media_id: Number(values?.media?.value),
                media_code_id: Number(values?.section?.value),
                reporter_id: values?.reporter_id ? values?.reporter_id : null,
            });
        } catch (error: any) {
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        media: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).required(`${t("This is required")}`),
        section: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).required(`${t("This is required")}`),
        reporter_id: Yup.string().nullable(),
    });

    const formik = useFormik({
        initialValues: {
            media: null,
            section: null,
            reporter_id: '',
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {

        const _options: any = medias?.map((item) => ({
            label: item?.name || '', value: String(item?.id),
            sections: item?.partner_codes?.map((d: any) => ({ label: d?.section_title, value: String(d?.id) })),
            reporters: item?.partner_reporters?.map((d: any) => ({ label: `${d?.reporter_id}`, value: String(d?.reporter_id), is_default: d?.is_default }))
        }));

        const _info_media = _options?.[0];
        const _info_section = _info_media?.sections?.[0];
        const _info_reporter = _info_media?.reporters?.find((_r: any) => !!_r?.is_default)?.value || _info_media?.reporters?.[0]?.value || '';
        setOptions((_prev: any) => _options);
        setTimeout(() => {
            formik.setFieldValue(`media`, _info_media);
            formik.setFieldValue(`section`, _info_section);
            formik.setFieldValue(`reporter_id`, _info_reporter);
        }, 300);

        return () => {
            formik.setErrors({});
        }
    }, [JSON.stringify(medias)]);

    return (
        <>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="md"
                scrollable={false}
            >
                <ModalHeader toggle={handleCloseClick}>
                    <>{t("Button Publish News")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Row>
                                <Col sm={12} md={12} xl={12} className="mt-3">
                                    <Label htmlFor="media" className="form-label">{t('Media')}<span className="text-danger"> *</span></Label>
                                    <DropdownOption
                                        name="media"
                                        dataList={options || []}
                                        placeholder={`${t("Media")}...`}
                                        className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                        classNamePrefix="name-prefix"
                                        initialValue={formik?.values?.media}
                                        onChangeSelect={(e: any) => {
                                            formik.setFieldValue(`media`, e);
                                        }}
                                    />
                                    {formik.touched.media && formik.errors.media ? (
                                        <div className="text-danger mt-2">{formik.errors.media}</div>
                                    ) : null}
                                </Col>
                                <Col sm={12} md={12} xl={12} className="mt-3">
                                    <Label htmlFor="section" className="form-label">{t('Section')}<span className="text-danger"> *</span></Label>
                                    <DropdownOption
                                        name="section"
                                        dataList={options?.find((op: any) => (String(op?.value) === String((formik?.values?.media as any)?.value)))?.sections || []}
                                        placeholder={`${t("Section")}...`}
                                        className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                        classNamePrefix="name-prefix"
                                        initialValue={formik?.values?.section}
                                        onChangeSelect={(e: any) => {
                                            formik.setFieldValue(`section`, e);
                                        }}
                                    />
                                    {formik.touched.section && formik.errors.section ? (
                                        <div className="text-danger mt-2">{formik.errors.section}</div>
                                    ) : null}
                                </Col>
                                <Col sm={12} md={12} xl={12} className="mt-3">
                                    <Label htmlFor="reporter" className="form-label">{t('Reporter ID')}</Label>
                                    <Input
                                        name={`reporter_id`}
                                        type="text"
                                        className="form-control"
                                        id={`reporter_id`}
                                        placeholder={t('Reporter ID')}
                                        value={formik?.values?.reporter_id}
                                        onChange={(event: any) => {
                                            formik.setFieldValue(`reporter_id`, event?.target?.value);
                                            // setInfoPublish((_prev: any) => ({ ..._prev, reporter: event?.target?.value }));
                                        }}
                                    />
                                    {formik.touched.reporter_id && formik.errors.reporter_id ? (
                                        <div className="text-danger mt-2">{formik.errors.reporter_id}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} md={12} xl={12} className='mt-4 mb-3 pt-1 text-end'>
                                    <button
                                        style={{ width: i18n?.language === 'en' ? '120px' : '90px' }}
                                        type="submit"
                                        disabled={isPublishing}
                                        className="btn btn-primary fs-14">
                                        {isPublishing ? <Spinner size="sm me-2" ></Spinner> : <></>}
                                        {t('Publish')}
                                    </button>
                                </Col>
                            </Row>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default React.memo(ModalPartner);
